import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Edit, Trash2, AlertTriangle } from 'lucide-react';
import ContentLoader from '../../components/ui/ContentLoader';
import { getProduct, deleteProduct } from '../../services/api';
import { Product } from '../../types';
import Button from '../../components/ui/Button';
import Modal, { ModalFooter } from '../../components/ui/Modal';
import ProductDetailView from '../../components/products/ProductDetailView';
import TenantFormModal from '../../components/tenants/TenantFormModal';

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch product data
  useEffect(() => {
    if (productId) {
      fetchProduct(productId);
    }
  }, [productId]);
  
  const fetchProduct = async (id: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getProduct(id);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Failed to load product data');
      toast.error('Failed to load product data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle edit product
  const handleEditProduct = () => {
    setIsEditModalOpen(true);
  };
  
  // Handle delete product
  const handleDeleteProduct = async () => {
    if (!product) return;
    
    setIsDeleting(true);
    
    try {
      await deleteProduct(product.id as string);
      toast.success('Product deleted successfully');
      navigate('/products');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-6">
        <ContentLoader message="Loading product details..." size="lg" />
      </div>
    );
  }
  
  if (error || !product) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <AlertTriangle size={48} className="text-amber-500 mb-4" />
            <h3 className="text-xl font-medium mb-2">Product Not Found</h3>
            <p className="text-text-muted-dark text-center max-w-md mb-6">
              {error || "We couldn't find the product you're looking for."}
            </p>
            <Button variant="primary" onClick={() => navigate('/products')} leftIcon={<ArrowLeft size={16} />}>
              Back to Products
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/products')} 
            className="mr-4"
            leftIcon={<ArrowLeft size={16} />}
          >
            Back
          </Button>
          <h1 className="text-2xl font-bold">Product Details</h1>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => setIsDeleteModalOpen(true)}
            leftIcon={<Trash2 size={16} />}
          >
            Delete
          </Button>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={handleEditProduct}
            leftIcon={<Edit size={16} />}
          >
            Edit
          </Button>
        </div>
      </div>
      
      {/* Main content */}
      <div className="card">
        <ProductDetailView 
          product={product} 
          onEdit={handleEditProduct} 
        />
      </div>
      
      {/* Edit Modal */}
      {isEditModalOpen && (
        <TenantFormModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            // Refresh product data when modal closes
            if (productId) {
              fetchProduct(productId);
            }
          }}
          tenantId={product.id as string}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Product"
        variant="danger"
      >
        <div className="py-4">
          <p className="mb-4">
            Are you sure you want to delete <strong>{product.name}</strong>? This action cannot be undone.
          </p>
          <p className="text-sm text-amber-400 bg-amber-950/30 border border-amber-800/30 p-3 rounded">
            <AlertTriangle size={16} className="inline-block mr-2" />
            Deleting this product will remove it from all tenant integrations.
          </p>
        </div>
        
        <ModalFooter
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteProduct}
          cancelText="Cancel"
          confirmText="Delete Product"
          isLoading={isDeleting}
          confirmVariant="danger"
        />
      </Modal>
    </div>
  );
};

export default ProductDetailPage; 